import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { X, Send, Copy, Check, ChevronDown, Loader2, Mail, ShieldCheck } from "lucide-react";
import { api } from "../../lib/api";
import { ROLES, ROLE_LABEL, ROLE_BLURB } from "../../lib/roles";
import { apiError } from "../../lib/api";
import { PremiumInput } from "../brand/PremiumInput";

/**
 * InviteModal — Linear/Notion-style invite-by-email with copyable invite link.
 * Steps:
 *   1. Compose:   email + role → POST /api/team/invites
 *   2. Sent:      shows the generated invite_url with copy + send another
 */
export const InviteModal = ({ open, onClose, onCreated }) => {
  const [step, setStep] = useState("compose"); // compose | sent
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("sales_negotiator");
  const [roleOpen, setRoleOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState(null); // { invite_url, email, role_label }
  const [copied, setCopied] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setStep("compose");
    setEmail("");
    setRole("sales_negotiator");
    setError("");
    setCreated(null);
    setCopied(false);
    setRoleOpen(false);
    setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const submit = async (e) => {
    e?.preventDefault();
    const cleaned = email.trim().toLowerCase();
    if (!cleaned || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(cleaned)) {
      setError("Enter a valid work email");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/api/team/invites", { email: cleaned, role });
      setCreated(data);
      setStep("sent");
      onCreated?.(data);
      toast.success("Invite sent", {
        description: `${cleaned} can now join as ${data.role_label}.`,
      });
    } catch (err) {
      const msg = apiError(err);
      setError(msg);
      toast.error("Couldn't send invite", { description: msg });
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = async () => {
    if (!created?.invite_url) return;
    try {
      const url = created.invite_url.startsWith("http")
        ? created.invite_url
        : `${window.location.origin}${created.invite_url}`;
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Invite link copied to clipboard");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy — please copy manually");
    }
  };

  const sendAnother = () => {
    setStep("compose");
    setEmail("");
    setRole("sales_negotiator");
    setCreated(null);
    setError("");
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const fullUrl = created?.invite_url
    ? (created.invite_url.startsWith("http")
        ? created.invite_url
        : `${window.location.origin}${created.invite_url}`)
    : "";

  return (
    <div
      className="fixed inset-0 z-[80] flex items-start justify-center px-4 pt-[10vh] bg-slate-950/55 backdrop-blur-sm"
      data-testid="invite-modal-backdrop"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div
        className="relative w-full max-w-[480px] rounded-2xl bg-white border border-slate-200/80 shadow-[0_24px_60px_-12px_rgba(15,23,42,0.28)] overflow-hidden"
        data-testid="invite-modal"
        role="dialog"
        aria-modal="true"
      >
        {/* Decorative aurora */}
        <div className="pointer-events-none absolute -top-24 -right-16 w-56 h-56 rounded-full bg-gradient-to-br from-indigo-300/35 to-blue-300/0 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-20 -left-16 w-48 h-48 rounded-full bg-gradient-to-br from-fuchsia-300/30 to-indigo-200/0 blur-3xl" />

        {/* Header */}
        <div className="relative flex items-start gap-3 px-6 pt-6 pb-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-700 flex items-center justify-center text-white shadow-[0_8px_20px_-8px_rgba(99, 102, 241,0.6)]">
            {step === "sent" ? <ShieldCheck size={18} /> : <Mail size={18} />}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-[19px] font-bold tracking-tight text-slate-900">
              {step === "sent" ? "Invite is ready to share" : "Invite a team member"}
            </h3>
            <p className="text-[12.5px] text-slate-500 mt-0.5 leading-snug">
              {step === "sent"
                ? "Copy the link below and send it via your favourite channel — they'll join your workspace in seconds."
                : "They'll receive a secure link to join your ChainFlow workspace."}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="invite-modal-close"
            className="-mt-1 -mr-1 w-8 h-8 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center transition-colors"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="relative px-6 pb-6">
          {step === "compose" && (
            <form onSubmit={submit} data-testid="invite-form">
              {/* Email */}
              <label className="block text-[10.5px] font-semibold uppercase tracking-[0.18em] text-slate-500 mb-1.5">
                Work email
              </label>
              <PremiumInput
                ref={inputRef}
                type="email"
                placeholder="teammate@agency.co.uk"
                value={email}
                onChange={setEmail}
                autoComplete="off"
                size="lg"
                testid="invite-email-input"
              />

              {/* Role picker */}
              <label className="block text-[10.5px] font-semibold uppercase tracking-[0.18em] text-slate-500 mb-1.5 mt-4">
                Role
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setRoleOpen((o) => !o)}
                  className="w-full h-11 pl-3.5 pr-3 rounded-xl bg-white border border-slate-200 hover:border-slate-300 text-left flex items-center justify-between transition-all"
                  data-testid="invite-role-trigger"
                >
                  <span className="flex flex-col">
                    <span className="text-[13.5px] font-semibold text-slate-900">{ROLE_LABEL[role]}</span>
                    <span className="text-[11px] text-slate-500 truncate">{ROLE_BLURB[role]}</span>
                  </span>
                  <ChevronDown size={15} className={`text-slate-400 transition-transform ${roleOpen ? "rotate-180" : ""}`} />
                </button>
                {roleOpen && (
                  <div className="absolute left-0 right-0 mt-2 z-10 rounded-xl bg-white border border-slate-200 shadow-[0_18px_40px_-10px_rgba(15,23,42,0.22)] overflow-hidden cf-fade-up" data-testid="invite-role-menu">
                    {ROLES.map((r) => (
                      <button
                        type="button"
                        key={r.id}
                        onClick={() => { setRole(r.id); setRoleOpen(false); }}
                        className={`w-full text-left px-3.5 py-2.5 hover:bg-indigo-50/60 flex flex-col gap-0.5 transition-colors ${role === r.id ? "bg-indigo-50/80" : ""}`}
                        data-testid={`invite-role-${r.id}`}
                      >
                        <span className="text-[13px] font-semibold text-slate-900 flex items-center gap-2">
                          {r.label}
                          {role === r.id && <Check size={13} className="text-indigo-600" />}
                        </span>
                        <span className="text-[11px] text-slate-500">{r.blurb}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {error && (
                <p className="mt-3 text-[12.5px] text-rose-600" data-testid="invite-error">{error}</p>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={submitting}
                className="mt-5 w-full h-11 rounded-xl text-white text-[13.5px] font-semibold inline-flex items-center justify-center gap-2 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                style={{
                  background: "linear-gradient(180deg, #6366F1 0%, #4F6BFF 100%)",
                  boxShadow: "0 10px 28px -10px rgba(99, 102, 241,0.55), inset 0 1px 0 rgba(255,255,255,0.18)",
                }}
                data-testid="invite-submit"
              >
                {submitting ? <><Loader2 size={14} className="animate-spin" /> Sending invite…</> : <><Send size={14} /> Send invite</>}
              </button>
              <p className="mt-3 text-[11px] text-slate-400 leading-snug">
                Invites expire after 14 days. They can be revoked any time from the team page.
              </p>
            </form>
          )}

          {step === "sent" && created && (
            <div className="space-y-4" data-testid="invite-sent-state">
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/70 px-3.5 py-3 flex items-start gap-3">
                <ShieldCheck size={16} className="text-emerald-600 mt-0.5" />
                <div className="text-[12.5px] text-emerald-800 leading-snug">
                  <span className="font-semibold">{created.email}</span> will join as <span className="font-semibold">{created.role_label}</span>.
                  Email delivery is coming soon — share the link below for now.
                </div>
              </div>

              <div>
                <label className="block text-[10.5px] font-semibold uppercase tracking-[0.18em] text-slate-500 mb-1.5">
                  Invite link
                </label>
                <div className="flex items-stretch gap-2">
                  <input
                    readOnly
                    value={fullUrl}
                    onFocus={(e) => e.target.select()}
                    className="flex-1 h-11 px-3.5 rounded-xl bg-slate-50 border border-slate-200 text-[12.5px] text-slate-700 font-mono outline-none"
                    data-testid="invite-link-input"
                  />
                  <button
                    type="button"
                    onClick={copyLink}
                    className={`h-11 px-3.5 rounded-xl border text-[12.5px] font-semibold inline-flex items-center gap-2 transition-all ${
                      copied
                        ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                        : "bg-slate-900 hover:bg-slate-800 border-slate-900 text-white"
                    }`}
                    data-testid="invite-copy-link"
                  >
                    {copied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-2 pt-1">
                <button
                  type="button"
                  onClick={sendAnother}
                  className="flex-1 h-11 rounded-xl bg-white border border-slate-200 hover:border-slate-300 text-slate-700 text-[13px] font-semibold transition-all"
                  data-testid="invite-send-another"
                >
                  Invite someone else
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 h-11 rounded-xl text-white text-[13px] font-semibold transition-all"
                  style={{
                    background: "linear-gradient(180deg, #6366F1 0%, #4F6BFF 100%)",
                    boxShadow: "0 10px 24px -10px rgba(99, 102, 241,0.55)",
                  }}
                  data-testid="invite-done"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
